/**
 * Inline password strength feedback for signup and reset-password.
 *
 * Renders a 4-segment bar that fills in as the score improves, plus a one-line
 * label + hint. Uses semantic color tokens so it tracks light/dark theming
 * without per-mode rules.
 */
import { scorePassword } from "@/lib/password-strength";

const SEGMENT_TONE: Record<0 | 1 | 2 | 3 | 4, string> = {
  0: "bg-destructive",
  1: "bg-warning",
  2: "bg-warning",
  3: "bg-success",
  4: "bg-success",
};

const LABEL_TONE: Record<0 | 1 | 2 | 3 | 4, string> = {
  0: "text-destructive",
  1: "text-warning-ink",
  2: "text-warning-ink",
  3: "text-success-ink",
  4: "text-success-ink",
};

export function PasswordStrengthMeter({
  value,
  "data-testid": testId,
}: {
  value: string;
  /**
   * Caller-supplied (signup and reset-password both render one). Worth having
   * because the component returns null for an empty password — without an id
   * a test cannot tell "rated weak" from "not rendered at all".
   */
  "data-testid"?: string;
}) {
  if (!value) return null;
  const { score, label, hint } = scorePassword(value);
  return (
    <div className="space-y-1.5" aria-live="polite" data-testid={testId}>
      <div className="flex gap-1" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
              i < score ? SEGMENT_TONE[score] : "bg-muted"
            }`}
          />
        ))}
      </div>
      <p className="text-xs">
        <span
          className={`font-medium ${LABEL_TONE[score]}`}
          data-testid={testId ? `${testId}-title` : undefined}
        >
          {label}
        </span>
        <span className="text-muted-foreground"> · {hint}</span>
      </p>
    </div>
  );
}
